import { BackspaceIcon } from '@phosphor-icons/react'
import { formatMoney } from '@paybae/shared'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Teclado numérico propio.
 *
 * No se usa el del sistema por tres razones concretas: en iOS el teclado tapa
 * media pantalla y esconde el resto del formulario, sus teclas son más chicas
 * que el mínimo táctil recomendado, y aparecer y desaparecer provoca un salto
 * de layout en cada toque. Uno propio ocupa un espacio fijo, con teclas de 56px
 * que no fallan al escribir rápido con una mano.
 *
 * El valor se maneja como cadena de centavos y se desplaza a la izquierda, que
 * es como funcionan las terminales de pago: se teclea 4-5-0 y aparece $4.50.
 * Evita tener que pensar dónde va el punto decimal.
 */

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', 'del'] as const

export function NumPad({
  valueCents,
  onChange,
  max = 99_999_999,
}: {
  valueCents: number
  /**
   * Debe aceptar la forma funcional de React (`setState(prev => …)`).
   *
   * Cada pulsación se calcula sobre el valor anterior real, no sobre el prop:
   * dos toques dentro del mismo ciclo de render verían el mismo prop obsoleto y
   * el segundo pisaría al primero. Con dedos humanos casi nunca ocurre, pero
   * depender de la velocidad de tecleo para que un monto se escriba bien no es
   * una garantía aceptable en una app de dinero.
   */
  onChange: React.Dispatch<React.SetStateAction<number>>
  max?: number
}) {
  const press = (key: string): void => {
    haptic('light')

    if (key === 'del') {
      onChange((prev) => Math.floor(prev / 10))
      return
    }

    const digits = key === '00' ? 2 : 1
    onChange((prev) => {
      const next = prev * 10 ** digits + Number(key === '00' ? 0 : key)
      return next > max ? prev : next
    })
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {KEYS.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => press(key)}
          aria-label={key === 'del' ? 'Borrar' : key}
          className={cn(
            'flex h-14 items-center justify-center rounded-card text-xl font-medium',
            'bg-surface-sunken text-ink',
            'transition-transform duration-100 active:scale-95',
            key === 'del' && 'text-ink-soft',
          )}
        >
          {key === 'del' ? <BackspaceIcon size={22} /> : key}
        </button>
      ))}
    </div>
  )
}

/** Monto en curso, en grande. Muestra $0 en gris hasta que se teclea algo. */
export function AmountDisplay({ cents }: { cents: number }) {
  return (
    <div className="flex items-baseline justify-center py-2">
      <span
        data-money
        className={cn(
          'font-display text-[clamp(2.5rem,12vw,3.5rem)] font-extrabold leading-none tracking-[-0.04em]',
          cents === 0 ? 'text-ink-faint' : 'text-ink',
        )}
      >
        {formatMoney(cents, { hideZeroCents: cents % 100 === 0 })}
      </span>
    </div>
  )
}
